import React, { useEffect, useState } from "react";
import { getInvoices } from "../../firebase/invoice/getInvoices";
import { getAllReturnedItems } from "../../firebase/returns/getAllReturnedItems";
import { getPayments } from "../../firebase/payment/getPayments";
import { InvoiceData } from "../../models/invoice";
import { PaymentData } from "../../models/payment";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableFooter,
	Paper,
	Typography,
	Button,
	Box
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { exportOutstandingInvoicesToPdf } from "../../utils/exportOutstandingInvoicesToPdf";
import { useAuth } from "../../context/AuthContext";

type InvoiceWithBalance = {
	invoice: InvoiceData;
	balance: number;
	totalAmount: number;
	amountPaid: number;
};
interface CreditInvoicesProps {
	getOutstanding?: (totalOutstanding: number) => void;
}
export const CreditInvoices: React.FC<CreditInvoicesProps> = ({
	getOutstanding
}) => {
	const { user } = useAuth();
	const [invoicesWithBalance, setInvoicesWithBalance] = useState<
		InvoiceWithBalance[]
	>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [exportData, setExportData] = useState<InvoiceWithBalance[]>([]);

	useEffect(() => {
		let isActive = true;

		const fetchData = async () => {
			if (!user?.uid) {
				if (isActive) {
					setInvoicesWithBalance([]);
					setExportData([]);
					setLoading(false);
				}
				return;
			}

			setLoading(true);
			try {
				const invoicesObj: { [key: string]: InvoiceData } =
					await getInvoices();
				const allInvoices: InvoiceData[] = Object.values(invoicesObj);
				// orderby date descending
				allInvoices.sort(
					(a, b) =>
						new Date(b.createdAt).getTime() -
						new Date(a.createdAt).getTime()
				);
				// Fetch only credit payments
				const creditPayments: PaymentData[] = await getPayments();
				const allReturnedItems = await getAllReturnedItems();

				const invoicesWithCredit = allInvoices.reduce(
					(acc: InvoiceWithBalance[], invoice: InvoiceData) => {
						const totalAmount = invoice.items.reduce(
							(
								sum: number,
								item: { sellPrice: number; quantity: number }
							) => sum + item.sellPrice * item.quantity,
							0
						);
						const paymentsForInvoice = creditPayments.filter(
							(p: PaymentData) =>
								p.invoiceNumber === invoice.invoiceNumber
						);
						const amountPaid = paymentsForInvoice.reduce(
							(sum: number, p: PaymentData) => sum + p.amount,
							0
						);
						const invoiceReturns =
							allReturnedItems[invoice.invoiceNumber] || [];
						const returnAmount = invoiceReturns.reduce(
							(sum: number, item) =>
								sum + item.amount * item.quantity,
							0
						);
						const balance = totalAmount - amountPaid - returnAmount;
						if (balance > 0) {
							acc.push({
								invoice,
								balance,
								totalAmount,
								amountPaid
							});
						}
						return acc;
					},
					[]
				);

				if (!isActive) {
					return;
				}

				setInvoicesWithBalance(invoicesWithCredit);
				setExportData(invoicesWithCredit);
			} catch (error) {
				console.error("Failed to load credit invoices:", error);
				if (isActive) {
					setInvoicesWithBalance([]);
					setExportData([]);
				}
			} finally {
				if (isActive) {
					setLoading(false);
				}
			}
		};

		void fetchData();
		return () => {
			isActive = false;
		};
	}, [user?.uid]);

	if (loading) {
		return <Typography>Loading...</Typography>;
	}

	// Calculate total balance
	const totalBalance = invoicesWithBalance.reduce(
		(sum, inv) => sum + inv.balance,
		0
	);
	if (getOutstanding) {
		getOutstanding(totalBalance);
	}

	return (
		<>
			<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
				<Button
					variant="outlined"
					size="small"
					startIcon={<PictureAsPdfIcon />}
					onClick={() =>
						exportOutstandingInvoicesToPdf(
							exportData.map((invoice) => ({
								invoiceNumber: invoice.invoice.invoiceNumber,
								customerName: invoice.invoice.customerName,
								mobileNumber: invoice.invoice.mobileNumber,
								totalAmount: invoice.totalAmount,
								amountPaid: invoice.amountPaid,
								balance: invoice.balance
							}))
						)
					}
				>
					Export PDF
				</Button>
			</Box>
			<TableContainer component={Paper}>
				<Table
					sx={{
						"& .MuiTableCell-root": {
							border: "1px solid rgba(224, 224, 224, 1)"
						},
						"& .MuiTableCell-head": {
							fontWeight: "bold"
						}
					}}
				>
					<TableHead>
						<TableRow>
							<TableCell>Invoice Number</TableCell>
							<TableCell>Customer Name</TableCell>
							<TableCell>Mobile Number</TableCell>
							<TableCell>Total Amount</TableCell>
							<TableCell>Amount Paid</TableCell>
							<TableCell>Balance</TableCell>
							<TableCell align="center">View</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{invoicesWithBalance.map(
							({ invoice, balance, totalAmount, amountPaid }) => (
								<TableRow key={invoice.invoiceNumber}>
									<TableCell>
										{invoice.invoiceNumber}
									</TableCell>
									<TableCell>
										{invoice.customerName}
									</TableCell>
									<TableCell>
										{invoice.mobileNumber}
									</TableCell>
									<TableCell>
										₹{totalAmount.toFixed(2)}
									</TableCell>
									<TableCell>
										₹{amountPaid.toFixed(2)}
									</TableCell>
									<TableCell>₹{balance.toFixed(2)}</TableCell>
									<TableCell align="center">
										<Button
											variant="contained"
											size="small"
											onClick={() =>
												window.open(
													`/invoice?invoiceNumber=${invoice.invoiceNumber}`,
													"_blank"
												)
											}
											startIcon={<VisibilityIcon />}
										>
											View
										</Button>
									</TableCell>
								</TableRow>
							)
						)}
					</TableBody>
					<TableFooter>
						<TableRow>
							<TableCell colSpan={5} align="right">
								<strong>Total Outstanding Balance</strong>
							</TableCell>
							<TableCell>
								<h5 style={{ color: "red" }}>
									₹{totalBalance.toFixed(2)}
								</h5>
							</TableCell>
						</TableRow>
					</TableFooter>
				</Table>
			</TableContainer>
		</>
	);
};
